import { useState } from "react";
import { Check, Gift, Sparkles } from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { SignInPanel } from "./SignInPanel";
import { BILLING_CONFIG } from "../lib/billingConfig";
import { useFlag } from "../lib/flags";
import type { SignInSurface } from "../lib/events";

/** What the visitor tried to do. Leads the pitch with that feature. */
export type CreateAccountFeature = "layout" | "import";

const LEAD: Record<CreateAccountFeature, { headline: string; sub: string }> = {
  layout: {
    headline: "Get Feng Shui layouts free",
    sub: "Create a free account and apply classical feng shui to this room, with the reason behind every move.",
  },
  import: {
    headline: "Import furniture free",
    sub: "Create a free account and turn any product link into a true-to-size piece for your room.",
  },
};

/**
 * The account ask in front of the AI features. Leads with "create a free
 * account" and what it includes; returning visitors switch to "Sign in".
 * Both modes use the same providers (Google creates the account on first
 * use), so only the copy changes.
 */
export function CreateAccountSheet({
  open,
  feature,
  surface,
  onClose,
  onSignedIn,
}: {
  open: boolean;
  feature: CreateAccountFeature;
  surface: SignInSurface;
  onClose: () => void;
  onSignedIn: () => void;
}) {
  const [mode, setMode] = useState<"create" | "signin">("create");
  const trialOn = useFlag("reverse_trial");
  const credits = BILLING_CONFIG.freeMonthlyCredits;
  const lead = LEAD[feature];

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={mode === "create" ? "Create your free account" : "Sign in"}
      side="right"
      width="min(440px, 96vw)"
    >
      <div data-testid="ai-sign-in-sheet" data-mode={mode} className="flex flex-col gap-4 pt-1">
        {mode === "create" ? (
          <>
            <div className="rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 p-4 text-white">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-label font-semibold uppercase tracking-wider">
                <Sparkles className="size-3" aria-hidden />
                Free account
              </div>
              <h2 className="mt-3 text-xl font-bold leading-tight">{lead.headline}</h2>
              <p className="mt-1 text-sm leading-snug text-white/85">{lead.sub}</p>
            </div>

            <ul className="flex flex-col gap-2.5" data-testid="create-account-benefits">
              <Benefit
                title={`${credits} free credits every month`}
                detail="Feng Shui layouts, room scores, or product imports. Resets monthly."
              />
              <Benefit
                title="Every feature, free"
                detail="Feng shui layouts, product import from any link, and 3D walk-throughs."
              />
              <Benefit
                title="Your rooms on every device"
                detail="Saved to your account, ready on your phone and your laptop."
              />
            </ul>

            {trialOn && (
              <div
                className="flex items-start gap-2.5 rounded-xl bg-violet-50 px-3 py-2.5 ring-1 ring-violet-200"
                data-testid="create-account-trial"
              >
                <Gift className="mt-0.5 size-4 shrink-0 text-violet-700" aria-hidden />
                <p className="text-body-sm leading-snug text-ink-700">
                  <span className="font-semibold text-ink-900">Bonus for new accounts:</span> 7 days of
                  Premium with {BILLING_CONFIG.premiumIncludedCredits} credits. No card needed.
                </p>
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="text-xl font-bold leading-tight text-ink-900">Welcome back</h2>
            <p className="mt-1 text-sm leading-snug text-ink-500">
              Sign in to pick up your rooms and your monthly credits.
            </p>
          </div>
        )}

        {/* One mount across both modes so the prompt is counted once. */}
        <SignInPanel surface={surface} intro={null} onSignedIn={onSignedIn} />

        <p className="text-center text-body-sm text-ink-500">
          {mode === "create" ? "Already have an account? " : "New here? "}
          <button
            type="button"
            onClick={() => setMode(mode === "create" ? "signin" : "create")}
            className="font-semibold text-violet-700 underline underline-offset-2 hover:text-violet-800"
            data-testid="create-account-switch"
          >
            {mode === "create" ? "Sign in" : "Create a free account"}
          </button>
        </p>
      </div>
    </Sheet>
  );
}

function Benefit({ title, detail }: { title: string; detail: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-violet-600 text-white">
        <Check className="size-3.5" strokeWidth={3} aria-hidden />
      </span>
      <div>
        <div className="text-body-sm font-semibold text-ink-900">{title}</div>
        <div className="text-caption leading-snug text-ink-500">{detail}</div>
      </div>
    </li>
  );
}
